/**
 * How a walking enemy keeps its soles on the ground as drawn (enemyFeel.ts stepUnit): the height under its footprint,
 * a step in that height eased out instead of snapped, and — for a boss or a rare, whose feet stand a body-length
 * apart — a pitch and roll with the slope, so neither end of it sinks into a ramp while the other hangs off it.
 *
 * Pure: the ground is a height function handed in, so the tests drive it with a made-up slope. Nothing here allocates.
 */

/** A height field: world y of the ground at a world point. */
export type HeightFn = (x: number, z: number) => number;

export const GROUND_FOLLOW = {
  /**
   * The steepest the drawn ground may rise or fall between two frames, per unit walked, and still be followed exactly.
   * The terrain's ramps stay under ~1.6 (terrain.ts: a smoothstep over at least `MinRamp`); anything sharper is a step
   * — a plank deck's lip, a cell seam of the lateral wander — and is eased out by the springs below.
   */
  maxGrade: 2.5,
  /** Slack on the grade test for a unit standing still while the ground is re-sampled (world units). */
  gradeSlack: 0.002,
  /** Critically damped spring rates (rad/s) easing a step: fast when the soles are below the ground, gentler going down. */
  upOmega: 34, downOmega: 16,
  /** The most a tilting unit pitches or rolls with the slope (rad, ~49°: a four-legged body climbs the steepest ramps nose-up, its knees taking the rest — footIk.ts), and how fast it turns to it (rad/s). */
  tiltMax: 0.85, tiltOmega: 14,
  /** The most a unit's root stands above the ground under its middle (world units): see `footing`. */
  maxLift: 0.06,
  /**
   * An upright unit (a raider on two legs) does not tilt: its body rides this share of the way up to where no sole is
   * buried, so on a slope the uphill sole is barely in the ground and the downhill one barely lifts.
   */
  uprightLift: 0.5,
  /**
   * How much of the slope a boss or rare tilts with: a long, four-legged body (`bodyTilt`) all of it; a big biped this
   * share, a lean with the ground rather than a fall forward.
   */
  bipedTilt: 0.35,
  /** A body at least this long (front to back) for its height stands on four legs. */
  quadLength: 0.92,
  /** The footprint: this share of the model's half-depth and half-width, where the soles actually stand. */
  footShare: 0.75,
  /** The smallest footprint sampled (world units), so a tiny unit still reads the slope it stands on. */
  minHalf: 0.04,
} as const;

/** One unit's ground contact: its smoothed height, the step still being eased out, and its tilt with the slope. */
export interface GroundTrack {
  /** Half-length (along its facing) and half-width of where its soles stand, in world units. */
  half: number; side: number;
  /** How much of the slope it tilts with: 0 upright (a raider), 1 a four-legged body, a share between for a big biped. */
  tilt: number;
  /** The height the root stands at this frame, and the ground target it eases toward. */
  y: number; target: number;
  /** The step left to ease out (y − target) and its rate. */
  e: number; ev: number;
  /** Pitch (nose up +) and roll (left side up +) with the slope, and their rates. */
  pitch: number; pv: number; roll: number; rv: number;
  /** How uneven the ground under its footprint was at the last `footing` (world units): its soles need placing. */
  relief: number;
  /** Where it was sampled last and the way it last faced (unit vector), for the grade test and a unit standing still. */
  lx: number; lz: number; fx: number; fz: number;
  /** False until the first sample: a new spawn stands on the ground at once. */
  set: boolean;
}

/** A track for a unit whose soles span `half` × `side` (half-depth × half-width, world units). */
export function groundTrack(half: number, side: number, tilt: number): GroundTrack {
  const s = GROUND_FOLLOW.footShare, m = GROUND_FOLLOW.minHalf;
  return {
    half: Math.max(m, half * s), side: Math.max(m, side * s), tilt: Math.max(0, Math.min(1, tilt)),
    y: 0, target: 0, e: 0, ev: 0, pitch: 0, pv: 0, roll: 0, rv: 0, relief: 0, lx: 0, lz: 0, fx: 0, fz: 1, set: false,
  };
}

/** Forget the last spawn's ground: the next sample stands it there at once. */
export function resetTrack(t: GroundTrack): void {
  t.set = false; t.e = t.ev = t.pitch = t.pv = t.roll = t.rv = 0;
}

/** The shared answer of `footing` (one per call site, reused). */
export interface Footing { y: number; pitch: number; roll: number }

/**
 * The ground under a footprint at (x, z) facing (fx, fz): the height its root stands at so no sole is buried, and
 * the pitch and roll that lay a tilting unit's soles on the slope (0 for an upright one).
 *
 * Front/back and left/right soles are sampled. A tilting unit turns to the slope (as far as `tiltMax` allows); its
 * root then rises until no sole, at that tilt, is inside the ground. An upright unit cannot turn, so it rises only
 * `uprightLift` of that way. Either rises at most `maxLift` over the ground under its middle: in a fold of the ground
 * (a valley floor, the foot of a steep ramp) a rigid body would bridge it and stand visibly in the air, so there the
 * soles press into the bank a little instead.
 */
export function footing(h: HeightFn, x: number, z: number, fx: number, fz: number, t: GroundTrack, out: Footing): Footing {
  const G = GROUND_FOLLOW, a = t.half, w = t.side;
  const c = h(x, z);
  const f = h(x + fx * a, z + fz * a), b = h(x - fx * a, z - fz * a);
  // left of the facing (fx, fz) is (fz, −fx) in x/z when y is up and the facing turns toward +x as yaw grows
  const l = h(x + fz * w, z - fx * w), r = h(x - fz * w, z + fx * w);
  const pitch = clampTilt(Math.atan2(f - b, 2 * a)) * t.tilt;
  const roll = clampTilt(Math.atan2(l - r, 2 * w)) * t.tilt;
  const ta = a * Math.tan(pitch), ts = w * Math.tan(roll);
  // the root height at which each sole, tilted, just touches the ground
  const need = Math.max(c, f - ta, b + ta, l - ts, r + ts);
  const lift = (need - c) * (t.tilt > 0 ? 1 : G.uprightLift);
  out.y = c + Math.min(lift, G.maxLift);
  out.pitch = pitch; out.roll = roll;
  t.relief = Math.max(Math.abs(f - c), Math.abs(b - c), Math.abs(l - c), Math.abs(r - c));
  return out;
}

function clampTilt(a: number): number { const m = GROUND_FOLLOW.tiltMax; return a > m ? m : a < -m ? -m : a; }

/**
 * Advances a track to this frame's ground `target` under (x, z), with the tilt it wants. A change the unit's own
 * walk explains (no steeper than `maxGrade` over the ground it covered) is followed exactly, so a unit on a ramp is
 * never behind its ground; the rest — a step — is kept as an offset that a critically damped spring eases out.
 */
export function followGround(t: GroundTrack, target: number, pitch: number, roll: number, x: number, z: number, dt: number): number {
  if (!t.set) {
    t.set = true; t.y = t.target = target; t.e = t.ev = 0;
    t.pitch = pitch; t.roll = roll; t.pv = t.rv = 0; t.lx = x; t.lz = z;
    return t.y;
  }
  const G = GROUND_FOLLOW;
  const moved = Math.hypot(x - t.lx, z - t.lz);
  const allow = G.maxGrade * moved + G.gradeSlack;
  const d = target - t.target;
  const step = d > allow ? d - allow : d < -allow ? d + allow : 0;
  t.e -= step;                                          // the body stays where it was; the step is eased out below
  t.target = target; t.lx = x; t.lz = z;
  const was = t.e;
  damp(t, "e", "ev", 0, was < 0 ? G.upOmega : G.downOmega, dt);
  if (was * t.e <= 0 || Math.abs(t.e) < 1e-5) { t.e = 0; t.ev = 0; }   // never past the ground: no overshoot
  damp(t, "pitch", "pv", pitch, G.tiltOmega, dt);
  damp(t, "roll", "rv", roll, G.tiltOmega, dt);
  t.y = target + t.e;
  return t.y;
}

type Springy = "e" | "pitch" | "roll";
type Rate = "ev" | "pv" | "rv";

/** Exact critically damped step of t[k] toward `goal` at rate `w` over dt (stable for any dt). */
function damp(t: GroundTrack, k: Springy, kv: Rate, goal: number, w: number, dt: number): void {
  const x0 = t[k] - goal, v0 = t[kv];
  const ex = Math.exp(-w * dt), q = v0 + w * x0;
  t[k] = goal + (x0 + q * dt) * ex;
  t[kv] = (v0 - w * q * dt) * ex;
}

/** The tilt share for a boss or rare whose model is `depth` long (front to back) and `height` tall; 0 for the rest. */
export function bodyTilt(tilts: boolean, depth: number, height: number): number {
  if (!tilts) return 0;
  return depth >= GROUND_FOLLOW.quadLength * height ? 1 : GROUND_FOLLOW.bipedTilt;
}

/** How uneven the ground is within `r` of (x, z): the most any of four samples round it differs from the middle. */
export function unevenUnder(h: HeightFn, x: number, z: number, r: number): number {
  const c = h(x, z);
  return Math.max(Math.abs(h(x + r, z) - c), Math.abs(h(x - r, z) - c), Math.abs(h(x, z + r) - c), Math.abs(h(x, z - r) - c));
}
